import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_ORIGIN } from "../config/apiConfig";
import PasswordChecklist, { PasswordInput, isPasswordValid } from "./PasswordField";

const API_BASE = API_ORIGIN;

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [linkValid, setLinkValid] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [userInfo, setUserInfo] = useState(null);

  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setLinkValid(false);
      setLinkError("This link is missing a token.");
      setChecking(false);
      return;
    }

    fetch(`${API_BASE}/validate_invite_token?token=${encodeURIComponent(token)}`)
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok && data.valid) {
          setLinkValid(true);
          setUserInfo(data);
        } else {
          setLinkValid(false);
          setLinkError(data.error || "This invite link is no longer valid.");
        }
      })
      .catch(() => {
        setLinkValid(false);
        setLinkError("Could not verify this link. Please try again.");
      })
      .finally(() => setChecking(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!isPasswordValid(password)) {
      setFormError("Password does not meet all the requirements below.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/set_password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password, confirmPassword: password }),
      });
      const data = await res.json();

      if (!res.ok) {
        setFormError(data.error || "Failed to set password.");
        return;
      }

      setSuccess(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setFormError("Network error while setting the password.");
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="container mt-5" style={{ maxWidth: 480 }}>
        <p>Checking your invite link...</p>
      </div>
    );
  }

  if (!linkValid && !success) {
    return (
      <div className="container mt-5" style={{ maxWidth: 480 }}>
        <div className="alert alert-danger">{linkError}</div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="container mt-5" style={{ maxWidth: 480 }}>
        <div className="alert alert-success">
          Your password has been created and your account is now active.
          Redirecting to login...
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5" style={{ maxWidth: 480 }}>
      <h4 className="mb-3" style={{ fontWeight: 700, textTransform: "uppercase" }}>Create Password</h4>
      {userInfo && (
        <p className="text-muted">
          Setting up account for <strong>{userInfo.UserName}</strong> (
          {userInfo.Email})
        </p>
      )}

      {formError && <div className="alert alert-danger">{formError}</div>}

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Enter Password:</label>
          <PasswordInput
            id="password"
            inputClassName="form-control"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder=""
            autoComplete="new-password"
          />
          <PasswordChecklist password={password} />
        </div>

        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? "Saving..." : "Create Password & Activate Account"}
        </button>
      </form>
    </div>
  );
}